import { Box, Divider, Link as MuiLink, Stack, Tooltip, Typography } from '@mui/material'
import { FeatureFlagIdAiTicketing } from 'c1gen/features'
import { AccessconflictV1AccessConflictServiceowner, AppV1Appsowner, AppV1AppUserServiceowner } from 'c1gen/roles'
import { AppAvatar } from 'components/core/avatar'
import { C1DisplayNameSkeleton, DisplayNameDetails } from 'components/core/display-name-details'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1StyledLink } from 'components/core/links/C1Link'
import { LastLoginDisplay } from 'components/core/time/display/LastLoginDisplay'
import { VariableText } from 'components/core/variable-text'
import { useFeatureChecker, useHasRole } from 'components/no-ui/capability'
import { convertToSentenceCase } from 'components/no-ui/string-formatting'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { ResourceTypeDisplayName } from 'components/pages/entitlement/resource-type/ResourceTypeDisplayName'
import { TaskEditDuration } from 'components/pages/task/details/content/TaskEditDuration'
import { TaskKeyValueMetadataRow } from 'components/pages/task/details/content/TaskKeyValueMetadataRow'
import { RenderPbStructRequestData } from 'components/pages/task/details/content/TaskRequestedData'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { TaskValue } from 'lib/actions/task'
import { SystemAnyAccessEntitlementID } from 'lib/constants/system'
import { routes, unsafeRoute } from 'lib/routes'
import { BadgeCheck, Clock, FileText, LayoutGrid, ListChecks, Lock, LogIn, MessageSquare, Shapes, Ticket, TriangleAlert, User } from 'lucide-react'
import { ActionServiceGetRequest } from 'pbts/c1/api/action/v1/action'
import { AccessConflictOutcome } from 'pbts/c1/api/analysis/v1/analysis'
import { GetAppRequest } from 'pbts/c1/api/app/v1/app'
import { GetAppEntitlementRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { FormSchemaServiceGetRequest } from 'pbts/c1/api/form/v1/form'
import { ResolvedFormDataServiceGetByTaskIdRequest } from 'pbts/c1/api/task/v1/task'
import { Any } from 'pbts/google/protobuf/any'
import { FC, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getActionApi } from 'redux_toolkit/actions'
import { appGetApi } from 'redux_toolkit/apps'
import { getAppEntitlementApi } from 'redux_toolkit/entitlements'
import { getFormSchemaApi } from 'redux_toolkit/form-schemas'
import { getResolvedFormDataApi } from 'redux_toolkit/tasks'

type TaskAdditionalInfoProps = {
    task: TaskValue
}

export const TaskAdditionalInfo: FC<TaskAdditionalInfoProps> = ({ task }) => {
    const intl = useIntl()

    const isAppOwner = useHasRole(AppV1Appsowner)
    const isAppUserOwner = useHasRole(AppV1AppUserServiceowner)

    const isAppAccessEntitlement = task.appEntitlements?.[0]?.id === SystemAnyAccessEntitlementID

    const { data: appEntitlement, isLoading } = getAppEntitlementApi.useGet(GetAppEntitlementRequest.create({
        appId: task.app?.id,
        id: task.appEntitlements?.[0]?.id,
        expandMask: { paths: ['*'] },
    }), { actionable: !!(task.app?.id && task.appEntitlements?.[0]?.id) })

    const idUser = task.identityUser || task.user

    return <Stack gap={2}>
        <AdditionalInformationAccessReview task={task} />

        {idUser && <TaskKeyValueMetadataRow
            Icon={User}
            label={intl.formatMessage({ id: 'components_task_dialog_requested_for', defaultMessage: 'Requested for' })}
            value={<UserDisplayName
                user={idUser}
                readOnly={!isAppOwner}
                margin={false}
                includeDetails={false}
                showTooltip
            />}
        />}

        <AdditionalInformationConflictWarning task={task} />

        {task.app && <TaskKeyValueMetadataRow
            Icon={LayoutGrid}
            label={intl.formatMessage({ id: 'components_task_dialog_app', defaultMessage: 'App' })}
            value={<AppDisplayName app={task.app} margin={false} readOnly={!isAppOwner} />}
        />}

        {task.appUser && <TaskKeyValueMetadataRow
            Icon={BadgeCheck}
            label={intl.formatMessage({ id: 'components_task_dialog_account', defaultMessage: 'Account' })}
            value={<AppUserDisplayName
                appUser={task.appUser}
                margin={false}
                includeDetails={false}
                readOnly={!isAppUserOwner}
                showTooltip
            />}
        />}

        {task.appUser && <TaskKeyValueMetadataRow
            Icon={LogIn}
            label={intl.formatMessage({ id: 'components_task_dialog_last_login', defaultMessage: 'Last login' })}
            value={<LastLoginDisplay lastUsedAt={task.appUser.lastUsedAt} />}
        />}

        {!isAppAccessEntitlement && task.type?.taskType.oneofKind !== 'action' && <>
            <TaskKeyValueMetadataRow
                Icon={Shapes}
                label={intl.formatMessage({ id: 'components_task_dialog_resource_type', defaultMessage: 'Resource type' })}
                value={
                    isLoading
                        ? <C1DisplayNameSkeleton />
                        : appEntitlement?.appResourceType
                            ? <ResourceTypeDisplayName
                                resourceType={appEntitlement.appResourceType}
                                margin={false}
                            />
                            : <MinimalAPIError err={<FormattedMessage id="component_api_error_resource_type" defaultMessage="Error fetching resource type" />} />
                }
            />

            <TaskKeyValueMetadataRow
                Icon={Lock}
                label={intl.formatMessage({ id: 'components_task_dialog_entitlement', defaultMessage: 'Entitlement' })}
                value={
                    isLoading
                        ? <C1DisplayNameSkeleton />
                        : appEntitlement
                            ? <C1EntitlementDisplayName entitlement={appEntitlement} readOnly={!isAppOwner} excludeDetails showTooltip />
                            : <MinimalAPIError err={<FormattedMessage id="component_api_error_entitlement" defaultMessage="Error fetching entitlement" />} />
                }
            />

            {appEntitlement?.description && <TaskKeyValueMetadataRow
                Icon={FileText}
                label={intl.formatMessage({ id: 'components_task_dialog_entitlement_description', defaultMessage: 'Description' })}
                value={<VariableText variableText={appEntitlement.description} />}
            />}
        </>}

        {task.type && task.type.taskType.oneofKind === 'grant' && <TaskKeyValueMetadataRow
            Icon={Clock}
            label={intl.formatMessage({ id: 'components_task_dialog_duration', defaultMessage: 'Duration' })}
            sx={{ mb: 1 }}
            value={
                <TaskEditDuration task={task} appEntitlement={appEntitlement} />
            }
        />}

        <AdditionalInformationTaskReason task={task} />

        <AdditionalInformationExternalTicket task={task} />

        <AdditionalActionAppDisplayName task={task} />

        <TaskActionFormInfo task={task} />
    </Stack>
}

type AdditionalInformationConflictWarningProps = {
    task: TaskValue
}

const AdditionalInformationConflictWarning: FC<AdditionalInformationConflictWarningProps> = ({ task }) => {
    const [showingAllMonitors, setShowingAllMonitors] = useState(false)

    const isAccessConflictOwner = useHasRole(AccessconflictV1AccessConflictServiceowner)

    const accessConflictOutcome = task.insights?.map((insight): AccessConflictOutcome | undefined => {
        if (insight.kind.oneofKind === 'analysis') {
            if (Any.typeUrlToName(insight.kind.analysis.outcome?.outcome?.typeUrl || '') === AccessConflictOutcome.typeName) {
                return insight.kind.analysis.outcome?.outcome as unknown as AccessConflictOutcome
            }
        }
        return undefined
    }).filter((outcome): outcome is AccessConflictOutcome => outcome !== undefined)[0]

    if (!accessConflictOutcome) {
        return null
    }

    if (!accessConflictOutcome.monitors.length) return null

    const monitorsToShow = accessConflictOutcome.monitors.slice(0, 4)
    const additionalMonitorsCount = accessConflictOutcome.monitors.length - 4

    return <TaskKeyValueMetadataRow
        Icon={TriangleAlert}
        label={<FormattedMessage id="components_insights_access_conflict_warning" defaultMessage="Warning" />}
        color="error.main"
        value={
            <>
                {(showingAllMonitors ? accessConflictOutcome.monitors : monitorsToShow).map((monitorInsight) => (
                    <Typography key={monitorInsight.monitorId}>
                        <FormattedMessage
                            id="components_insights_access_conflict"
                            defaultMessage="This access {taskType, select, grant {will create} certify {triggered} other {will create}} alerts in the <link>{monitor_name}</link> conflict monitor"
                            values={{
                                monitor_name: monitorInsight.monitorDisplayName,
                                taskType: task.type?.taskType.oneofKind,
                                link: (chunks) => (
                                    isAccessConflictOwner ? (
                                        <C1StyledLink
                                            sx={{ color: 'inherit' }}
                                            target="_blank"
                                            href={routes.conflictMonitors.detail(monitorInsight.monitorId)}
                                        >
                                            {chunks}
                                        </C1StyledLink>
                                    ) : (
                                        <strong>{chunks}</strong>
                                    )
                                ),
                            }}
                        />
                    </Typography>
                ))}
                {(additionalMonitorsCount > 0 && !showingAllMonitors) && (
                    <Typography variant="caption">
                        <MuiLink sx={{ color: 'inherit' }} onClick={(): void => setShowingAllMonitors(!showingAllMonitors)}>
                            <FormattedMessage
                                id="components_insights_access_conflict_additional"
                                defaultMessage="+ {count, plural, one {# other monitor} other {# other monitors}}"
                                values={{ count: additionalMonitorsCount }}
                            />
                        </MuiLink>
                    </Typography>
                )}
            </>
        }
    />
}

type AdditionalInformationAccessReviewProps = {
    task: TaskValue
}

export const AdditionalInformationAccessReview: FC<AdditionalInformationAccessReviewProps> = ({ task }) => (
    task.accessReview && <TaskKeyValueMetadataRow
        Icon={ListChecks}
        label={<FormattedMessage id="components_task_dialog_campaign" defaultMessage="Campaign" />}
        sx={{ mb: 1 }}
        value={<Tooltip title={<Box>
            <Typography variant="h6">{task.accessReview.displayName}</Typography>
            <Typography>
                <VariableText
                    variableText={task.accessReview.description}
                    emptyState={
                        <Typography variant="unconfigured" sx={{ color: 'info.contrastText' }}>
                            <FormattedMessage id="components_task_dialog_campaign_empty" defaultMessage="This campaign has no description." />
                        </Typography>
                    }
                />
            </Typography>
        </Box>}
        >
            <Typography>
                {task.accessReview.displayName}
            </Typography>
        </Tooltip>}
    />
)

type AdditionalInformationTaskReasonProps = {
    task: TaskValue
}

const AdditionalInformationTaskReason: FC<AdditionalInformationTaskReasonProps> = ({ task }) => (
    task.type?.taskType.oneofKind !== 'certify' && task.type?.taskType.oneofKind !== 'action' && <TaskKeyValueMetadataRow
        Icon={MessageSquare}
        label={<FormattedMessage id="components_task_dialog_reason" defaultMessage="Reason" />}
        value={
            <VariableText
                variableText={task.description}
                sx={{
                    '&:before': { content: 'open-quote' },
                    '&:after': { content: 'close-quote' },
                }}
            />
        }
    />
)

type AdditionalInformationExternalTicketProps = {
    task: TaskValue
}

const AdditionalInformationExternalTicket: FC<AdditionalInformationExternalTicketProps> = ({ task }) => {
    const hasAiTicketing = useFeatureChecker(FeatureFlagIdAiTicketing)

    if (!hasAiTicketing || !task.externalRefs || task.externalRefs.length === 0) {
        return null
    }

    const externalRef = task.externalRefs.at(0)
    if (!externalRef || !externalRef.url) {
        return null
    }

    const taskId = externalRef.url.split('/').at(-1)

    return <TaskKeyValueMetadataRow
        Icon={Ticket}
        label={<FormattedMessage id="components_task_dialog_external_ref" defaultMessage="External ticket" />}
        value={
            <DisplayNameDetails
                avatar={<AppAvatar appName={externalRef.name || 'jira'} />}
                displayName={taskId || 'Link'}
                link={unsafeRoute(externalRef.url)}
            />

        }
    />
}

type AdditionalActionAppDisplayNameProps = {
    task: TaskValue
}

const AdditionalActionAppDisplayName: FC<AdditionalActionAppDisplayNameProps> = ({ task }) => {
    const intl = useIntl()
    const isAppOwner = useHasRole(AppV1Appsowner)

    // Only show for action task type
    const isActionTask = task.type?.taskType.oneofKind === 'action'
    const actionId = task.type?.taskType.oneofKind === 'action'
        ? task.type.taskType.action.actionId
        : undefined

    const { data: action, isLoading: isLoadingAction } = getActionApi.useGet(
        ActionServiceGetRequest.create({ id: actionId || '' }),
        { actionable: !!actionId },
    )

    // Only proceed if it's a baton resource action
    const isBatonAction = action?.targetRef.oneofKind === 'batonResourceActionRef'
    const appId = isBatonAction ? action.targetRef.batonResourceActionRef.appId : undefined

    const { data: app, isLoading: isLoadingApp } = appGetApi.useGet(
        GetAppRequest.create({ id: appId || '' }),
        { actionable: !!appId },
    )

    // Exit if not an action task
    if (!isActionTask) {
        return null
    }

    // Show loading while fetching action
    if (isLoadingAction) {
        return <TaskKeyValueMetadataRow
            Icon={LayoutGrid}
            label={intl.formatMessage({ id: 'components_task_dialog_app', defaultMessage: 'Application' })}
            value={<C1DisplayNameSkeleton />}
        />
    }

    // Exit if not a baton resource action
    if (!isBatonAction) {
        return null
    }

    // Show loading while fetching app
    if (isLoadingApp) {
        return <TaskKeyValueMetadataRow
            Icon={LayoutGrid}
            label={intl.formatMessage({ id: 'components_task_dialog_app', defaultMessage: 'Application' })}
            value={<C1DisplayNameSkeleton />}
        />
    }

    if (!app) {
        return null
    }

    return <Stack gap={2}>
        <TaskKeyValueMetadataRow
            Icon={LayoutGrid}
            label={intl.formatMessage({ id: 'components_task_dialog_app', defaultMessage: 'Application' })}
            value={<AppDisplayName app={app} margin={false} readOnly={!isAppOwner} />}
        />
        <TaskKeyValueMetadataRow
            Icon={Shapes}
            label={intl.formatMessage({ id: 'components_task_dialog_resource_type', defaultMessage: 'Resource type' })}
            value={action.targetRef.batonResourceActionRef.resourceTypeId
                ? convertToSentenceCase(action.targetRef.batonResourceActionRef.resourceTypeId)
                : action.targetRef.batonResourceActionRef.batonActionDisplayName}
        />
    </Stack>
}

type TaskActionFormInfoProps = {
    task: TaskValue
}

export const TaskActionFormInfo: FC<TaskActionFormInfoProps> = ({ task }) => {
    const actionId = task.type?.taskType.oneofKind === 'action' ? task.type.taskType.action.actionId : undefined
    const formValues = task.type?.taskType.oneofKind === 'action' ? task.type.taskType.action.formValues : undefined
    const isActionTask = task.type?.taskType.oneofKind === 'action'

    // Always fetch resolved form data for action tasks (removed approver check)
    const {
        data: resolvedFormData,
        isLoading: isLoadingResolved,
        error: resolvedFormError,
        isError: isResolvedFormError,
    } = getResolvedFormDataApi.useGetByTaskId(
        ResolvedFormDataServiceGetByTaskIdRequest.create({ taskId: task.id }),
        { actionable: isActionTask },
    )

    // Check if error is NOT_FOUND (expected for optional resolved form data)
    const isNotFoundError = resolvedFormError?.code === 'NOT_FOUND'
    const isRealError = isResolvedFormError && !isNotFoundError

    useEffect(() => {
        if (isActionTask && isRealError && resolvedFormError) {
            console.error('[TaskActionFormInfo] Resolved form data API error:', resolvedFormError)
        }
    }, [task.id, isActionTask, isRealError, resolvedFormError])

    const { data: action, isLoading: isLoadingAction } = getActionApi.useGet(
        ActionServiceGetRequest.create({ id: actionId || '' }),
        { actionable: !!actionId },
    )

    const formSchemaId = action?.formSchemaRef?.id

    const { data: formSchema, isLoading: isLoadingFormSchema } = getFormSchemaApi.useGet(
        FormSchemaServiceGetRequest.create({ id: formSchemaId || '' }),
        { actionable: !!formSchemaId },
    )

    // Always use resolved form data if available (post-CEL transformation), otherwise fall back to original
    // Check that we have both resolved values and form fields, and that fields is a non-empty array
    const hasCompleteResolvedFormData =
        resolvedFormData?.resolvedValues &&
        resolvedFormData?.form?.fields &&
        Array.isArray(resolvedFormData.form.fields) &&
        resolvedFormData.form.fields.length > 0
    const dataToDisplay = hasCompleteResolvedFormData ? resolvedFormData.resolvedValues : formValues
    const fieldsToDisplay = hasCompleteResolvedFormData ? resolvedFormData.form.fields : (formSchema?.form?.fields || [])

    if (task.type?.taskType.oneofKind !== 'action') {
        return null
    }

    if (formSchemaId === undefined) {
        return null
    }

    // Show loading state while fetching data
    if (isLoadingAction || isLoadingFormSchema || isLoadingResolved) {
        return <Stack gap={2} pt={2}>
            <Typography variant="h5">
                <FormattedMessage id="components_task_dialog_additional_info" defaultMessage="Additional info" />
            </Typography>
            <Divider />
            <C1DisplayNameSkeleton />
        </Stack>
    }

    if (!dataToDisplay || fieldsToDisplay.length === 0) {
        return null
    }

    return <Stack gap={2} pt={2}>
        <Typography variant="h5">
            <FormattedMessage id="components_task_dialog_additional_info" defaultMessage="Additional info" />
        </Typography>
        <Divider />
        <RenderPbStructRequestData data={dataToDisplay} fields={fieldsToDisplay} />
    </Stack>
}
